import React, { useEffect, useMemo, useState } from "react";
import Hero from "./components/Hero";
import GuessInput from "./components/GuessInput";
import GuessHistory from "./components/GuessHistory";
import ResultBanner from "./components/ResultBanner";
import StatsPanel from "./components/StatsPanel";
import { fetchDailyGame, submitGuessRequest } from "./lib/api";
import {
  buildShareText,
  defaultStats,
  getDailyStateKey,
  getPreviousDateKey,
  getStockholmDateKey,
  normalize,
} from "./utils/game";
import "./styles.css";

const MAX_GUESSES = 8;
const STATS_KEY = "korvdle:stats";

export default function App() {
  const [todayKey, setTodayKey] = useState(getStockholmDateKey());
  const [choices, setChoices] = useState([]);
  const [answer, setAnswer] = useState(null);
  const [query, setQuery] = useState("");
  const [guesses, setGuesses] = useState([]);
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(defaultStats());
  const [notice, setNotice] = useState("");
  const [shareNotice, setShareNotice] = useState("");
  const [hydrated, setHydrated] = useState(false);
  const [loading, setLoading] = useState(true);

  const guessedIds = useMemo(() => new Set(guesses.map((entry) => entry.guess.id)), [guesses]);

  const suggestions = useMemo(() => {
    const q = normalize(query);
    if (!q) return [];

    return choices
      .filter((sausage) => !guessedIds.has(sausage.id) && normalize(sausage.name).includes(q))
      .slice(0, 6);
  }, [query, choices, guessedIds]);

  useEffect(() => {
    async function bootstrap() {
      try {
        const daily = await fetchDailyGame();
        console.log("daily response:", daily);
        setTodayKey(daily.playDate);
        setChoices(daily.choices || []);

        const rawStats = localStorage.getItem(STATS_KEY);
        if (rawStats) {
          setStats({ ...defaultStats(), ...JSON.parse(rawStats) });
        }

        const rawDaily = localStorage.getItem(getDailyStateKey(daily.playDate));
        if (rawDaily) {
          const parsed = JSON.parse(rawDaily);
          setGuesses(parsed.guesses || []);
          setResult(parsed.result || null);
          setAnswer(parsed.answer || null);
        }
      } catch (error) {
        console.error("Bootstrap error:", error);
        setNotice(`Kunde inte ladda dagens spel: ${error.message}`);
      } finally {
        setHydrated(true);
        setLoading(false);
      }
    }

    bootstrap();
  }, []);

  useEffect(() => {
    if (!hydrated) return;

    try {
      localStorage.setItem(STATS_KEY, JSON.stringify(stats));
      localStorage.setItem(
        getDailyStateKey(todayKey),
        JSON.stringify({
          guesses,
          result,
          answer,
        })
      );
    } catch (error) {
      console.error("Kunde inte spara speldata", error);
    }
  }, [guesses, result, answer, stats, hydrated, todayKey]);

  useEffect(() => {
    if (!notice) return;
    const timeout = window.setTimeout(() => setNotice(""), 2400);
    return () => window.clearTimeout(timeout);
  }, [notice]);

  useEffect(() => {
    if (!shareNotice) return;
    const timeout = window.setTimeout(() => setShareNotice(""), 2200);
    return () => window.clearTimeout(timeout);
  }, [shareNotice]);

  function finalizeGame(nextResult, guessesUsed) {
    setResult(nextResult);

    setStats((current) => {
      const previousDay = getPreviousDateKey(todayKey);
      const continuedStreak = current.lastPlayedKey === previousDay;

      if (nextResult === "won") {
        return {
          played: current.played + 1,
          won: current.won + 1,
          streak: continuedStreak ? current.streak + 1 : 1,
          best: current.best == null ? guessesUsed : Math.min(current.best, guessesUsed),
          lastPlayedKey: todayKey,
        };
      }

      return {
        played: current.played + 1,
        won: current.won,
        streak: 0,
        best: current.best,
        lastPlayedKey: todayKey,
      };
    });
  }

  async function submitGuess(forcedName) {
    if (loading) return;

    if (result) {
      setNotice("Dagens runda är redan klar. Kom tillbaka imorgon för en ny korv.");
      return;
    }

    const inputName = normalize(forcedName || query);
    const match = choices.find((sausage) => normalize(sausage.name) === inputName);

    if (!match) {
      setNotice("Välj en korv från listan för att gissa.");
      return;
    }

    if (guessedIds.has(match.id)) {
      setNotice("Du har redan gissat på den korven.");
      return;
    }

    try {
      const response = await submitGuessRequest({
        sausageId: match.id,
        attemptNumber: guesses.length + 1,
      });

      const nextGuesses = [
        ...guesses,
        {
          guess: response.guess,
          comparison: response.comparison,
        },
      ];

      setGuesses(nextGuesses);
      setQuery("");

      if (response.isCorrect) {
        setAnswer(response.target);
        finalizeGame("won", nextGuesses.length);
        return;
      }

      if (nextGuesses.length >= MAX_GUESSES) {
        setAnswer(response.target);
        finalizeGame("lost", nextGuesses.length);
      }
    } catch (error) {
      console.error(error);
      setNotice("Kunde inte skicka gissningen.");
    }
  }

  async function shareResult() {
    if (!result) return;

    const text = buildShareText({
      guesses,
      todayKey,
      maxGuesses: MAX_GUESSES,
      result,
    });

    try {
      await navigator.clipboard.writeText(text);
      setShareNotice("Resultatet kopierades!");
    } catch (error) {
      console.error(error);
      setShareNotice(text);
    }
  }

  const winRate = stats.played ? Math.round((stats.won / stats.played) * 100) : 0;

  return (
    <div className="page-shell">
      <div className="page-glow page-glow-left" />
      <div className="page-glow page-glow-right" />

      <main className="app-layout">
        <Hero totalSausages={choices.length} guessesCount={guesses.length} maxGuesses={MAX_GUESSES} />

        <section className="content-grid">
          <div className="main-column">
            <GuessInput
              query={query}
              setQuery={setQuery}
              submitGuess={submitGuess}
              suggestions={suggestions}
              notice={notice}
              disabled={Boolean(result) || loading}
            />

            <ResultBanner
              result={result}
              target={answer || { name: "", note: "" }}
              shareResult={shareResult}
            />

            <GuessHistory
              guesses={guesses}
              maxGuesses={MAX_GUESSES}
            />
          </div>

          <aside className="side-column">
            <StatsPanel stats={stats} winRate={winRate} />

            <div className="glass-card info-card">
              <div className="card-title">Hur färgerna funkar</div>
              <p><strong>Grönt</strong> betyder exakt rätt.</p>
              <p><strong>Gult</strong> betyder nära träff, till exempel rätt region eller överlappande kött.</p>
              <p><strong>Pilar</strong> visar om dagens korv har högre eller lägre rökighet eller kryddighet.</p>
            </div>
          </aside>
        </section>
      </main>

      {shareNotice ? <div className="share-toast">{shareNotice}</div> : null}
    </div>
  );
}